import { trpc } from "@/lib/trpc";
import { formatSingaporePhoneE164 } from "@shared/userOnboarding";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useLocation, useParams } from "wouter";

export default function InviteAccept() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const [, setLocation] = useLocation();

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const inviteQuery = trpc.auth.getInviteDetails.useQuery(
    { token },
    {
      enabled: !!token,
      retry: false,
    }
  );

  const invitedEmail = inviteQuery.data?.email?.trim() ?? "";

  const acceptInvite = trpc.auth.acceptInvite.useMutation({
    onSuccess: () => {
      toast.success("Account activated successfully.");
      setLocation("/");
    },
    onError: (e) => toast.error(e.message),
  });

  const isInvalidToken = useMemo(
    () => inviteQuery.isError || !token,
    [inviteQuery.isError, token]
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    try {
      formatSingaporePhoneE164(phone);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Invalid input");
      return;
    }
    acceptInvite.mutate({ token, password, phone });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <header className="border-b border-gray-100 px-8 py-5 flex items-center justify-between">
        <span className="text-sm font-semibold tracking-normal">CLARITY</span>
        <span className="text-xs tracking-[0.2em] uppercase text-gray-400 font-medium">Service Portal</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-sm w-full">
          {inviteQuery.isLoading ? (
            <p className="text-sm text-muted-foreground text-center">Loading invite...</p>
          ) : isInvalidToken ? (
            <div className="text-center">
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Invalid Invite</h1>
              <p className="text-sm text-gray-400 mb-8">
                This invite link is invalid, expired, or already used.
              </p>
              <button
                type="button"
                onClick={() => setLocation("/login")}
                className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="text-center mb-2">
                <h1 className="text-2xl font-light tracking-wide text-black mb-2">Complete Your Account</h1>
                <p className="text-sm text-gray-400">
                  Welcome {inviteQuery.data?.userName || "there"} - set your details to activate access.
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                  Email Address
                </label>
                <p
                  className="w-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 rounded-none"
                  data-testid="invited-email"
                >
                  {invitedEmail}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This account will be activated using the email address the invitation was sent to.
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="91234567 or +6591234567"
                  required
                  className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors rounded-none"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Singapore mobile numbers will be formatted automatically.
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                  className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors rounded-none"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                  className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors rounded-none"
                />
              </div>

              <button
                type="submit"
                disabled={acceptInvite.isPending}
                className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50"
              >
                {acceptInvite.isPending ? "Activating..." : "Activate Account"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
